import React from "react";
import styled from "styled-components";

const ContactSectionWrapper = styled.section`
  padding: 72px 20px;
  background: #d7edff;
  box-sizing: border-box;
  > h3 {
    color: #133876;
    font-size: 36px;
  }
  @media (max-width: 768px) {
    padding: 48px 16px;
  }

  @media (max-width: 480px) {
    padding: 36px 14px;
    > h3 {
      font-size: 24px;
    }
  }
`;

const ContactWrapper = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(2, 34, 71, 0.12);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContactLeft = styled.div`
  background: #133876;
  color: white;
  padding: 34px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  overflow: hidden;
  min-height: 330px;

  &::after {
    content: "";
    position: absolute;
    bottom: -200px;
    right: -200px;
    width: 300px;
    height: 300px;
    background: #0b2145;
    border-radius: 100px 0 0 0;
    animation: contactOrbFloat 10s ease-in-out infinite;
  }

  h4 {
    font-size: 28px;
    margin-top: 0;
    @media (max-width: 768px) {
      font-size: 22px;
    }
    @media (max-width: 480px) {
      font-size: 18px;
    }
  }

  @media (max-width: 768px) {
    padding: 28px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 24px;
    gap: 8px;
  }

  @keyframes contactOrbFloat {
    0% {
      transform: translate(0, 0) scale(1);
      opacity: 0.5;
    }
    50% {
      transform: translate(-20px, -10px) scale(1.05);
      opacity: 0.7;
    }
    100% {
      transform: translate(0, 0) scale(1);
      opacity: 0.5;
    }
  }
`;

const ContactRight = styled.div`
  padding: 28px;

  @media (max-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 13px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 15px;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 13px 14px;
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 130px;
  padding: 11px 13px;
  border-radius: 8px;
  border: 1px solid #ddd;
  margin-bottom: 12px;
  box-sizing: border-box;
  resize: vertical;
  font-size: 15px;

  @media (max-width: 480px) {
    padding: 13px 14px;
    height: 110px;
    margin-bottom: 12px;
    font-size: 16px;
  }
`;

const SubmitBtn = styled.button`
  padding: 11px 18px;
  background: #0d1b2a;
  color: white;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 15px;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 13px 20px;
    font-size: 16px;
  }

  @media (max-width: 480px) {
    padding: 14px 20px;
    font-size: 16px;
  }
`;

const SubmitMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.4;
  border: 1px solid
    ${(props) => (props.$status === "success" ? "#34a853" : "#d93025")};
  background:
    ${(props) =>
      props.$status === "success"
        ? "rgba(52, 168, 83, 0.08)"
        : "rgba(217, 48, 37, 0.08)"};
  color: ${(props) => (props.$status === "success" ? "#155724" : "#611a15")};
`;

const SubmitMessageIcon = styled.span`
  font-size: 18px;
  line-height: 1;
  margin-top: 1px;
`;

const SubmitMessageText = styled.span`
  flex: 1;
`;

function ContactSection({
  firstNameRef,
  isSubmitting,
  submitMessage,
  submitStatus,
  onSubmit,
}) {
  return (
    <ContactSectionWrapper id="contact">
      <h3 style={{ textAlign: "center", marginBottom: 28 }}>Kontakt</h3>
      <ContactWrapper>
        <ContactLeft>
          <h4>Kontaktdaten</h4>
          <a
            href="tel:+41792022091"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            +41 79 202 20 91
          </a>
          <a
            href="mailto:info@sfm-trust.ch"
            style={{
              color: "inherit",
              textDecoration: "none",
              marginTop: 25,
              marginBottom: 25,
            }}
          >
            info@sfm-trust.ch
          </a>
          <a
            href="https://www.google.com/maps/search/?api=1&query=%22Swiss%20Financial%20Management%20KLG%2C%20Industriestrasse%2019%2C%208112%20Otelfingen%2C%20Switzerland%22"
            target="_blank"
            rel="noreferrer"
            style={{
              color: "inherit",
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            <span>
              Swiss Financial Management GmbH,
              <br />
              Bachtelweg 17, 8604 Volketswil
            </span>
          </a>
        </ContactLeft>

        <ContactRight>
          <form onSubmit={onSubmit} noValidate>
            <Input
              ref={firstNameRef}
              name="first_name"
              placeholder="Vorname"
              required
            />
            <Input name="last_name" placeholder="Nachname" required />
            <Input name="company" placeholder="Firma" />
            <Input name="email" type="email" placeholder="E-Mail" required />
            <Input name="phone" placeholder="Telefonnummer" />
            <TextArea
              name="description"
              placeholder="Ihre Nachricht..."
              required
            />
            <SubmitBtn type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
            </SubmitBtn>
            {submitMessage && (
              <SubmitMessage
                role="status"
                aria-live="polite"
                $status={submitStatus || "success"}
              >
                <SubmitMessageIcon>
                  {submitStatus === "error" ? "!" : "✓"}
                </SubmitMessageIcon>
                <SubmitMessageText>{submitMessage}</SubmitMessageText>
              </SubmitMessage>
            )}
          </form>
        </ContactRight>
      </ContactWrapper>
    </ContactSectionWrapper>
  );
}

export default ContactSection;
